import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MdLanguage,
  MdLibraryBooks,
  MdOutlineCalculate,
  MdOutlineScience,
  MdSchool
} from 'react-icons/md';
import { Img } from '@Components/Img';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import MultiChannelIcon from './Icons/MultiChannelIcon.svg?react';
import { About } from './Pages/About';
import { AllChannels } from './Pages/AllChannels';
import { Amazon } from './Pages/Amazon';
import { Commerce } from './Pages/Commerce';
import { Discovery } from './Pages/Discovery';
import { Display } from './Pages/Display';
import { GeoTesting } from './Pages/GeoTesting';
import { GoogleDV360 } from './Pages/GoogleDV360';
import { GoogleSearch } from './Pages/GoogleSearch';
import { Incrementality101 } from './Pages/Incrementality101';
import { IncrementalityAgent } from './Pages/IncrementalityAgent';
import { IncrementalityNavBar } from './IncrementalityNavBar';
import { LinearTV } from './Pages/LinearTV';
import { Meta } from './Pages/Meta';
import { MultiChannel } from './Pages/MultiChannel';
import { PerformanceMax } from './Pages/PerformanceMax';
import { Pinterest } from './Pages/Pinterest';
import { Reddit } from './Pages/Reddit';
import { Search } from './Pages/Search';
import { GhostBidding } from './Pages/GhostBidding';
import { Shopping } from './Pages/Shopping';
import { Snapchat } from './Pages/Snapchat';
import { Social } from './Pages/Social';
import { Spotify } from './Pages/Spotify';
import { Streaming } from './Pages/Streaming';
import { TikTok } from './Pages/TikTok';
import { TradeDesk } from './Pages/TradeDesk';
import { UserLevelCalculator } from './Pages/UserLevelCalculator';
import { YouTube } from './Pages/YouTube';
import { INC_CDN, navigateToSection } from './IncrementalityPlaybookShared';
import { equalsIgnoreCase } from '../../utils/text-utils';

export { INC_CDN, IncPlaybookSectionBox, navigateToSection } from './IncrementalityPlaybookShared';
export type { IncPlaybookSectionBoxProps } from './IncrementalityPlaybookShared';
export { PAGE_PREFIX } from './IncrementalityPlaybookShared';

export const INC_PLAYBOOK_PAGES: Record<
  'Channels' | 'Information' | 'Tools',
  Record<
    string,
    {
      content: ReactElement;
      icon?: ReactElement;
      subPages?: Record<string, ReactElement>;
    }
  >
> = {
  Information: {
    About: {
      content: <About />,
      icon: <MdLibraryBooks />
    },
    'Incrementality 101': { content: <Incrementality101 />, icon: <MdSchool /> }
  },
  Channels: {
    'All Channels': {
      content: <AllChannels />,
      icon: <MdLanguage />,
      subPages: {
        'Geo Testing': <GeoTesting />
      }
    },
    Commerce: {
      content: <Commerce />,
      icon: <Img src={`${INC_CDN}/Icon_ConnectedCommerce.png`} />,
      subPages: {
        Amazon: <Amazon />
      }
    },
    Display: {
      content: <Display />,
      subPages: {
        'Google (DV360)': <GoogleDV360 />,
        'The Trade Desk': <TradeDesk />
      }
    },
    'Linear TV': {
      content: <LinearTV />,
      icon: <Img src={`${INC_CDN}/Icon_LinearTV.png`} />
    },
    'Multi-Channel': {
      content: <MultiChannel />,
      icon: <MultiChannelIcon />,
      subPages: {
        Discovery: <Discovery />,
        'Performance Max': <PerformanceMax />
      }
    },
    Search: {
      content: <Search />,
      subPages: { 'Google Search': <GoogleSearch /> }
    },
    Shopping: {
      content: <Shopping />,
      icon: <Img src={`${INC_CDN}/Icon_Shoppable.png`} />
    },
    Social: {
      content: <Social />,
      subPages: {
        Meta: <Meta />,
        Pinterest: <Pinterest />,
        Reddit: <Reddit />,
        Snapchat: <Snapchat />,
        TikTok: <TikTok />
      }
    },
    Streaming: {
      content: <Streaming />,
      icon: <Img src={`${INC_CDN}/Icon_StreamingVideo.png`} />,
      subPages: {
        Spotify: <Spotify />,
        YouTube: <YouTube />,
        'Ghost Bidding': <GhostBidding />
      }
    }
  },
  Tools: {
    'User Level Calculator': { content: <UserLevelCalculator />, icon: <MdOutlineCalculate /> },
    'Incrementality Agent': { content: <IncrementalityAgent />, icon: <MdOutlineScience /> }
  }
};

export const FLAT_INC_PLAYBOOK_PAGES: Record<string, ReactElement> = (() => {
  const flatPages: Record<string, ReactElement> = {};
  for (const section of Object.keys(INC_PLAYBOOK_PAGES)) {
    for (const key of Object.keys(INC_PLAYBOOK_PAGES[section])) {
      flatPages[key] = INC_PLAYBOOK_PAGES[section][key].content;
      if (INC_PLAYBOOK_PAGES[section][key].subPages) {
        for (const subKey of Object.keys(INC_PLAYBOOK_PAGES[section][key].subPages)) {
          flatPages[subKey] = INC_PLAYBOOK_PAGES[section][key].subPages[subKey];
        }
      }
    }
  }
  return flatPages;
})();

const FLAT_INC_PLAYBOOK_PAGE_KEYS = Object.keys(FLAT_INC_PLAYBOOK_PAGES);

export const IncrementalityPlaybook = () => {
  const { '*': page } = useParams<{ '*'?: string }>();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState<string>(
    page && FLAT_INC_PLAYBOOK_PAGES[page] ? page : 'About'
  );
  const [rerender, setRerender] = useState<boolean>(false);

  const setActivePageURL = useCallback(
    (pageKey: string, hash?: string) => {
      for (const key of Object.keys(FLAT_INC_PLAYBOOK_PAGES)) {
        if (equalsIgnoreCase(pageKey, key.replace(/ /g, ''))) {
          navigate(`/incrementality-playbook/${key.replace(/ /g, '')}${hash ?? ''}`);
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    const pageFromURL = page ?? '';
    const routeKeys = Object.keys(FLAT_INC_PLAYBOOK_PAGES).map(key =>
      key.toLowerCase().replace(/ /g, '')
    );
    if (!pageFromURL || !routeKeys.includes(pageFromURL.toLowerCase())) {
      navigate('/incrementality-playbook/About', { replace: true });
      return;
    }
    const canonicalKey = Object.keys(FLAT_INC_PLAYBOOK_PAGES).find(key =>
      equalsIgnoreCase(key.replace(/ /g, ''), pageFromURL)
    );
    if (canonicalKey && canonicalKey !== activePage) {
      setActivePage(canonicalKey);
    }
    if (window.location.hash) {
      navigateToSection(window.location.hash.replace('#', ''), 'auto'); // smooth scrolling will scroll to wrong Y position as elements render on the page
    }
  }, [activePage, page, navigate]);

  useEffect(() => {
    const handleResize = () => setRerender(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (rerender) {
      setRerender(false);
    }
  }, [rerender]);

  const renderedPage = useMemo(
    () =>
      rerender ? (
        <Spinner size={100} />
      ) : (
        <div className='inc-playbook-page-container'>
          {FLAT_INC_PLAYBOOK_PAGES[activePage] ? (
            FLAT_INC_PLAYBOOK_PAGES[activePage]
          ) : (
            <Spinner size={100} />
          )}
        </div>
      ),
    [activePage, rerender]
  );

  return (
    <Page minWidth='500px' pageType='Incrementality Playbook' title='Incrementality Playbook'>
      <IncrementalityNavBar
        activePage={activePage}
        flatPageKeys={FLAT_INC_PLAYBOOK_PAGE_KEYS}
        pages={INC_PLAYBOOK_PAGES}
        setActivePageURL={setActivePageURL}
      />
      {renderedPage}
    </Page>
  );
};
